import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Popover,
  Button,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import BarChartIcon from "@mui/icons-material/BarChart";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import api from "../../axiosInstance";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
);

const barValueLabelsPlugin = {
  id: "barValueLabels",
  afterDatasetsDraw(chart, args, pluginOptions) {
    const { ctx } = chart;
    const color = pluginOptions?.color || "#555";
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value == null) return;
        ctx.save();
        ctx.fillStyle = color;
        ctx.font = "700 12px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(value, bar.x, bar.y - 8);
        ctx.restore();
      });
    });
  },
};

// Picks a "nice" round step (multiple of 1000) so consecutive ticks never
// round to the same ₹Xk label on the y-axis.
const getNiceThousandStep = (maxValue) => {
  if (!maxValue || maxValue <= 0) return 1000;
  const roughStep = maxValue / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  let niceResidual = 1;
  if (residual > 5) niceResidual = 10;
  else if (residual > 2) niceResidual = 5;
  else if (residual > 1) niceResidual = 2;
  return Math.max(1000, Math.round((niceResidual * magnitude) / 1000) * 1000);
};

const formatPercentTrend = (pct) => {
  if (pct == null) return "No change data";
  const rounded = Math.abs(Number(pct)).toFixed(1);
  return `${pct >= 0 ? "↑" : "↓"} ${rounded}%`;
};

const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value ?? 0));

const buildStats = (d = {}) => [
  {
    icon: <PeopleAltIcon sx={{ fontSize: 40, color: "#4caf50" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    label: "Total Mentors",
    value: `${d.totalMentors ?? 0}`,
    trend: `${d.activeMentors ?? 0} Active · ${d.pendingMentors ?? 0} Pending`,
    trendColor: "#66bb6a",
    trendUp: (d.mentorPercentage ?? 0) >= 0,
    glowColor: "rgba(129,199,132,0.18)",
  },
  {
    icon: <CalendarMonthIcon sx={{ fontSize: 40, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    label: "Total Sessions",
    value: `${d.totalSessions ?? 0}`,
    trend: formatPercentTrend(d.sessionPercentage),
    trendColor: "#42a5f5",
    trendUp: (d.sessionPercentage ?? 0) >= 0,
    glowColor: "rgba(100,181,246,0.18)",
  },
  {
    icon: <MonetizationOnIcon sx={{ fontSize: 40, color: "#ffb74d" }} />,
    iconBg: "linear-gradient(135deg, #fffdf0, #fff3cd)",
    borderColor: "#ffd54f",
    label: "Total Earnings",
    value: formatINR(d.totalEarnings),
    trend: formatPercentTrend(d.earningsPercentage),
    trendColor: "#ffa726",
    trendUp: (d.earningsPercentage ?? 0) >= 0,
    glowColor: "rgba(255,213,79,0.18)",
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 40, color: "#ab47bc" }} />,
    iconBg: "linear-gradient(135deg, #fdf4ff, #f3e5f5)",
    borderColor: "#ce93d8",
    label: "Avg Occupancy",
    value: `${d.averageOccupancy ?? 0}%`,
    trend: formatPercentTrend(d.occupancyPercentage),
    trendColor: "#ab47bc",
    trendUp: (d.occupancyPercentage ?? 0) >= 0,
    glowColor: "rgba(206,147,216,0.18)",
  },
  {
    icon: <StarIcon sx={{ fontSize: 40, color: "#ff8a65" }} />,
    iconBg: "linear-gradient(135deg, #fff5f2, #ffd5c8)",
    borderColor: "#ffab91",
    label: "Avg Rating",
    value: `${d.averageRating ?? 0}`,
    trend: `from ${d.totalReviews ?? 0} reviews`,
    trendColor: "#ff8a65",
    trendUp: (d.ratingPercentage ?? 0) >= 0,
    glowColor: "rgba(255,171,145,0.18)",
  },
];

const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.75)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

function TopListCard({ title, columnLabel, borderColor, glowColor, rows }) {
  return (
    <Card
      sx={{
        ...cardStyle,
        boxShadow: `0 4px 24px ${glowColor}, 0 1px 4px rgba(0,0,0,0.06)`,
        borderTop: `4px solid ${borderColor}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          fontWeight={700}
          color="#1a1a2e"
          sx={{ mb: 2 }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="#999"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {columnLabel}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            color="#999"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Bookings
          </Typography>
        </Box>

        {rows.length ? (
          rows.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1.1,
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#333", fontWeight: 500 }}
              >
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#1a1a2e", fontWeight: 700 }}
              >
                {row.count}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            No data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const MENTOR_ADMIN_FILTER_KEY = "MentorAdminDateFilter";

function MentorAdmin() {
  const savedFilter = (() => {
    try {
      return JSON.parse(localStorage.getItem(MENTOR_ADMIN_FILTER_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [activeFilter, setActiveFilter] = useState(
    savedFilter.activeFilter || "1M",
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(savedFilter.startDate || "");
  const [endDate, setEndDate] = useState(savedFilter.endDate || "");
  const [dashboardData, setDashboardData] = useState({});
  const [topSubjects, setTopSubjects] = useState([]);
  const [topMentors, setTopMentors] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [bookingVolumeTrend, setBookingVolumeTrend] = useState({});
  const [bookingStatusDistribution, setBookingStatusDistribution] = useState(
    {},
  );

  useEffect(() => {
    localStorage.setItem(
      MENTOR_ADMIN_FILTER_KEY,
      JSON.stringify({ activeFilter, startDate, endDate }),
    );
  }, [activeFilter, startDate, endDate]);

  const getDateRange = (filter, customStart, customEnd) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "custom") return { start: customStart, end: customEnd };
    if (filter === "TODAY") return { start: fmt(today), end: fmt(today) };
    if (filter === "YESTERDAY") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: fmt(yesterday), end: fmt(yesterday) };
    }
    const end = fmt(today);
    const start = new Date(today);
    if (filter === "1W") start.setDate(start.getDate() - 7);
    else if (filter === "1M") start.setMonth(start.getMonth() - 1);
    else if (filter === "3M") start.setMonth(start.getMonth() - 3);
    else if (filter === "6M") start.setMonth(start.getMonth() - 6);
    else start.setFullYear(start.getFullYear() - 1);
    return { start: fmt(start), end };
  };

  const { start: resolvedStart, end: resolvedEnd } = getDateRange(
    activeFilter,
    startDate,
    endDate,
  );

  useEffect(() => {
    if (activeFilter === "custom" && (!resolvedStart || !resolvedEnd)) return;
    api
      .get("/admin/mentors/dashboard", {
        params: { startDate: resolvedStart, endDate: resolvedEnd },
      })
      .then((res) => setDashboardData(res.data?.data || {}))
      .catch((err) => console.error("Mentor dashboard fetch error:", err));
  }, [resolvedStart, resolvedEnd, activeFilter]);

  useEffect(() => {
    if (activeFilter === "custom" && (!resolvedStart || !resolvedEnd)) return;
    const params = { startDate: resolvedStart, endDate: resolvedEnd };
    api
      .get("/admin/mentors/top-subjects", { params })
      .then((res) => setTopSubjects(res.data?.data || []))
      .catch((err) => console.error("Top subjects fetch error:", err));
    api
      .get("/admin/mentors/top-mentors", { params })
      .then((res) => setTopMentors(res.data?.data || []))
      .catch((err) => console.error("Top mentors fetch error:", err));
    api
      .get("/admin/mentors/revenue-trend", { params })
      .then((res) => setRevenueTrend(res.data?.data || []))
      .catch((err) => console.error("Revenue trend fetch error:", err));
    api
      .get("/admin/mentors/booking-volume-trend", { params })
      .then((res) => setBookingVolumeTrend(res.data?.data || {}))
      .catch((err) => console.error("Booking volume trend fetch error:", err));
    api
      .get("/admin/mentors/booking-status-distribution", { params })
      .then((res) => setBookingStatusDistribution(res.data?.data || {}))
      .catch((err) =>
        console.error("Booking status distribution fetch error:", err),
      );
  }, [resolvedStart, resolvedEnd, activeFilter]);

  const stats = buildStats(dashboardData);

  const filterLabel =
    activeFilter === "custom" && startDate && endDate
      ? `${startDate} – ${endDate}`
      : activeFilter === "TODAY"
        ? "Today"
        : activeFilter === "YESTERDAY"
          ? "Yesterday"
          : activeFilter === "1W"
            ? "Last Week"
            : activeFilter === "1M"
              ? "Last 1 Month"
              : activeFilter === "3M"
                ? "Last 3 Months"
                : activeFilter === "6M"
                  ? "Last 6 Months"
                  : "Last 1 Year";

  const handleFilterChange = (_, val) => {
    if (val) {
      setActiveFilter(val);
      setAnchorEl(null);
    }
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setActiveFilter("custom");
      setAnchorEl(null);
    }
  };

  const earningsData = {
    labels: revenueTrend.map((d) => d.date),
    datasets: [
      {
        label: "Earnings (₹)",
        data: revenueTrend.map((d) => d.revenue),
        fill: true,
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;
          if (!chartArea) return "rgba(255,183,77,0.15)";
          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(255,183,77,0.45)");
          gradient.addColorStop(1, "rgba(255,183,77,0.02)");
          return gradient;
        },
        borderColor: "#ffb74d",
        borderWidth: 2.5,
        pointBackgroundColor: "#ffb74d",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4,
      },
    ],
  };

  const earningsMax = Math.max(0, ...revenueTrend.map((d) => d.revenue ?? 0));
  const earningsStep = getNiceThousandStep(earningsMax);

  const earningsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.95)",
        titleColor: "#1a1a2e",
        bodyColor: "#ffb74d",
        borderColor: "#ffd54f",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx) => ` ₹ ${ctx.parsed.y.toLocaleString("en-IN")}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#9e9e9e", font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: {
          color: "#9e9e9e",
          font: { size: 12 },
          stepSize: earningsStep,
          callback: (val) => `₹${(val / 1000).toFixed(0)}k`,
        },
      },
    },
  };

  const doughnutData = {
    labels: ["Completed", "Upcoming", "Cancelled"],
    datasets: [
      {
        data: [
          bookingStatusDistribution.completedPercentage ?? 0,
          bookingStatusDistribution.upcomingPercentage ?? 0,
          bookingStatusDistribution.cancelledPercentage ?? 0,
        ],
        backgroundColor: ["#81c784", "#64b5f6", "#ff924f"],
        borderColor: ["#fff", "#fff", "#fff"],
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "58%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 16,
          font: { size: 12 },
          color: "#555",
          usePointStyle: true,
          pointStyleWidth: 16,
        },
      },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.95)",
        titleColor: "#1a1a2e",
        bodyColor: "#555",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed}%`,
        },
      },
    },
  };

  const bookingVolumeChart = bookingVolumeTrend.chart || [];

  const bookingVolumeData = {
    labels: bookingVolumeChart.map((d) => d.bookingDate),
    datasets: [
      {
        label: "Bookings",
        data: bookingVolumeChart.map((d) => d.bookingCount),
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;
          if (!chartArea) return "#60a5fa";
          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, "#2563eb");
          gradient.addColorStop(1, "#93c5fd");
          return gradient;
        },
        borderRadius: 8,
        maxBarThickness: 60,
      },
    ],
  };

  const bookingVolumeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 24 } },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
          boxWidth: 12,
          boxHeight: 12,
          color: "#555",
          font: { size: 12 },
        },
      },
      barValueLabels: { color: "#2563eb" },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.95)",
        titleColor: "#1a1a2e",
        bodyColor: "#2563eb",
        borderColor: "#bfdbfe",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} bookings`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#9e9e9e", font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.08)", borderDash: [4, 4] },
        ticks: { color: "#9e9e9e", font: { size: 11 } },
      },
    },
  };

  return (
    <Box sx={{ p: 2, paddingBottom: "50px" }}>
      {/* Filter Bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            background: "rgba(255,255,255,0.85)",
            border: "1px solid #e8eaf0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            backdropFilter: "blur(8px)",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              borderColor: "#bdbdbd",
            },
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16, color: "#667eea" }} />
          <Typography
            sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}
          >
            {filterLabel}
          </Typography>
          <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
              minWidth: 220,
              overflow: "hidden",
              border: "1px solid #f0f0f0",
            },
          }}
        >
          {/* Quick Options */}
          {[
            { label: "Today", value: "TODAY" },
            { label: "Yesterday", value: "YESTERDAY" },
            { label: "Last Week", value: "1W" },
            { label: "Last 1 Month", value: "1M" },
            { label: "Last 3 Months", value: "3M" },
            { label: "Last 6 Months", value: "6M" },
            { label: "Last 1 Year", value: "1Y" },
          ].map((opt) => (
            <Box
              key={opt.value}
              onClick={() => handleFilterChange(null, opt.value)}
              sx={{
                px: 2.5,
                py: 1.2,
                cursor: "pointer",
                fontSize: "0.83rem",
                fontWeight: activeFilter === opt.value ? 700 : 500,
                color: activeFilter === opt.value ? "#667eea" : "#444",
                background:
                  activeFilter === opt.value
                    ? "rgba(102,126,234,0.07)"
                    : "#fff",
                borderLeft:
                  activeFilter === opt.value
                    ? "3px solid #667eea"
                    : "3px solid transparent",
                transition: "all 0.15s",
                "&:hover": {
                  background: "rgba(102,126,234,0.06)",
                  color: "#667eea",
                },
              }}
            >
              {opt.label}
            </Box>
          ))}

          {/* Divider */}
          <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />

          {/* Custom Range Section */}
          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="#aaa"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 1,
                fontSize: "0.68rem",
              }}
            >
              Custom Range
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
                mt: 1,
              }}
            >
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setActiveFilter("1M");
                    setAnchorEl(null);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    borderColor: "#e0e0e0",
                    color: "#888",
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  onClick={handleCustomApply}
                  disabled={!startDate || !endDate}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    boxShadow: "none",
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
      {/* Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        {stats.map((stat, index) => (
          <Card
            key={index}
            sx={{
              ...cardStyle,
              boxShadow: `0 4px 24px ${stat.glowColor}, 0 1px 4px rgba(0,0,0,0.06)`,
              borderTop: `4px solid ${stat.borderColor}`,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 8px 32px ${stat.glowColor}, 0 2px 8px rgba(0,0,0,0.08)`,
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    background: stat.iconBg,
                    borderRadius: 2,
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 2px 8px ${stat.glowColor}`,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                      fontSize: "1.8rem",
                      color: "#1a1a2e",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {stat.trendUp ? (
                  <TrendingUpIcon
                    sx={{ fontSize: 18, color: stat.trendColor }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{ fontSize: 18, color: stat.trendColor }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: stat.trendColor,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                >
                  {stat.trend}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 2.5,
        }}
      >
        {/* Monthly Earnings Area Chart */}
        <Card
          sx={{
            ...cardStyle,
            boxShadow:
              "0 4px 24px rgba(255,213,79,0.15), 0 1px 4px rgba(0,0,0,0.06)",
            borderTop: "4px solid #ffd54f",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                  Monthly Earnings
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {resolvedStart} – {resolvedEnd}
                </Typography>
              </Box>
              <Box
                sx={{
                  background: "linear-gradient(135deg, #fffdf0, #fff3cd)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  border: "1px solid #ffd54f",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "#ffa726" }}
                >
                  {formatPercentTrend(dashboardData.earningsPercentage)}
                </Typography>
              </Box>
            </Box>
            {revenueTrend.length ? (
              <Box sx={{ height: 280 }}>
                <Line data={earningsData} options={earningsOptions} />
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: "center" }}
              >
                No data available
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Session Breakdown Doughnut Chart */}
        <Card
          sx={{
            ...cardStyle,
            boxShadow:
              "0 4px 24px rgba(100,181,246,0.15), 0 1px 4px rgba(0,0,0,0.06)",
            borderTop: "4px solid #64b5f6",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                  Sessions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  By session type
                </Typography>
              </Box>
              <Box
                sx={{
                  background: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  border: "1px solid #64b5f6",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2 }}
                >
                  Rescheduled
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: "#1565c0" }}
                >
                  {bookingStatusDistribution.rescheduledCount ?? 0}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ height: 280 }}>
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Booking Volume Trend + Top Lists Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "11fr 5fr 4fr" },
          gap: 2.5,
          mt: 2.5,
        }}
      >
        {/* Booking Volume Trend Chart */}
        <Card
          sx={{
            ...cardStyle,
            boxShadow:
              "0 4px 24px rgba(79,195,247,0.18), 0 1px 4px rgba(0,0,0,0.06)",
            borderTop: "4px solid #4fc3f7",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2.5,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1a1a2e"
                sx={{ pr: 1 }}
              >
                Booking Volume Trend
              </Typography>

              <Box sx={{ width: "1px", height: 36, background: "#e0e0e0" }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon sx={{ fontSize: 22, color: "#3f51b5" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Total Bookings
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#3f51b5", lineHeight: 1.1 }}
                  >
                    {bookingVolumeTrend.bookingCount ?? 0}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ width: "1px", height: 36, background: "#e0e0e0" }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShowChartIcon sx={{ fontSize: 22, color: "#43a047" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Avg Price
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "#1a1a2e", lineHeight: 1.1 }}
                  >
                    {formatINR(bookingVolumeTrend.averageBookingValue)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ width: "1px", height: 36, background: "#e0e0e0" }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TrendingUpIcon sx={{ fontSize: 22, color: "#7c4dff" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Bookings
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.7 }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{ color: "#7c4dff", lineHeight: 1.1 }}
                    >
                      {bookingVolumeTrend.bookingGrowthPercentage != null
                        ? `${Math.abs(bookingVolumeTrend.bookingGrowthPercentage).toFixed(0)}%`
                        : "0%"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {bookingVolumeTrend.bookingGrowthPercentage == null
                        ? "No change"
                        : bookingVolumeTrend.bookingGrowthPercentage >= 0
                          ? "increase"
                          : "decrease"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {bookingVolumeChart.length ? (
              <Box sx={{ height: 280 }}>
                <Bar
                  data={bookingVolumeData}
                  options={bookingVolumeOptions}
                  plugins={[barValueLabelsPlugin]}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 6, textAlign: "center" }}
              >
                No data available
              </Typography>
            )}
          </CardContent>
        </Card>

        <TopListCard
          title="Top Subjects"
          columnLabel="Subject"
          borderColor="#4db6ac"
          glowColor="rgba(77,182,172,0.18)"
          rows={topSubjects.map((s) => ({
            id: s.subjectId,
            label: s.subjectName,
            count: s.bookingCount,
          }))}
        />
        <TopListCard
          title="Top Mentors"
          columnLabel="Mentor"
          borderColor="#7986cb"
          glowColor="rgba(121,134,203,0.18)"
          rows={topMentors.map((m) => ({
            id: m.mentorId,
            label: m.mentorName,
            count: m.bookingCount,
          }))}
        />
      </Box>
    </Box>
  );
}

export default MentorAdmin;
